import React, { createContext, useContext, useEffect, useMemo, useCallback, useState } from 'react';
import type { AxiosError } from 'axios';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { EventType, Survey, SurveyResponsePayload, UserFilter } from 'types/survey';
import { Bitrix24Context } from './legacy/Bitrix24Context';
import { SystemContext } from './legacy/SystemContext';
import { atomicHub } from 'services/api';
import { loadPlanType } from 'utils/subscription/loadPlanType';

type AppDataReturn = {
	appQueryParams: URLSearchParams;
	userFilter: UserFilter;
};

interface SurveyContextType {
	fetchSurveyFakedoor: (surveyId: string) => Promise<void>;
	surveyAvailable: Survey | null;
	sendSurveyResponse: (payload: SurveyResponsePayload) => Promise<void>;
	sendSurveyEvent: (eventType: EventType) => Promise<void>;
	shouldOpen: boolean;
	setShouldOpen: (value: boolean) => void;
}

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

export const SurveyProvider = ({
	children,
	application,
}: {
	children: React.ReactNode;
	application: string;
}) => {
	const bitrix24 = useContext(Bitrix24Context);
	const { auth: authData, user } = bitrix24;
	const [surveyAvailable, setSurveyAvailable] = useState<Survey | null>(null);
	const [shouldOpen, setShouldOpen] = useState(false);
	const intl = useIntl();
	const { subscriptionReady, subscription, loadingSubscription } = useContext(SystemContext);

	const getApplicationData = useCallback(
		(displayFormat?: 'popup' | 'fakedoor'): AppDataReturn | null => {
			if (!authData || !user || !subscriptionReady || !subscription) return null;

			const planTypeFormatted =
				loadPlanType(loadingSubscription, subscription) === 'LEGACY'
					? 'trial'
					: loadPlanType(loadingSubscription, subscription).toLocaleLowerCase();

			const userFilter: UserFilter = {
				application,
				domain: authData.domain,
				internalId: user.ID ?? '',
				clientId: authData.member_id,
				email: user.EMAIL ?? '',
			};

			const displayConfig = {
				displayFormat: displayFormat || 'popup',
				planType: [planTypeFormatted],
				subscriptionState: [subscription.status],
			};

			const appQueryParams = new URLSearchParams();
			appQueryParams.append('application', application);
			appQueryParams.append('isActive', 'true');
			appQueryParams.append('displayConfig', JSON.stringify(displayConfig));
			appQueryParams.append('user', JSON.stringify(userFilter));

			return {
				appQueryParams,
				userFilter,
			};
		},
		[bitrix24, application, subscriptionReady, subscription],
	);

	const fetchSurvey = async (queryParams: URLSearchParams) => {
		try {
			const urlWithParams = `/public-surveys/filter?${queryParams.toString()}`;
			const response = await atomicHub.get(urlWithParams);
			if (response.data.length > 0) {
				setSurveyAvailable(response.data[0]);
				setShouldOpen(true);
			} else {
				setSurveyAvailable(null);
				setShouldOpen(false);
			}
		} catch (error) {
			console.error('Erro ao buscar pesquisas: ', error);
			setSurveyAvailable(null);
			setShouldOpen(false);
		}
	};

	const fetchSurveyPopup = useCallback(async () => {
		const appData = getApplicationData();

		if (!appData) return;

		const { appQueryParams } = appData;

		await fetchSurvey(appQueryParams);
	}, [getApplicationData]);

	const fetchSurveyFakedoor = useCallback(
		async (surveyId: string) => {
			const appData = getApplicationData('fakedoor');
			if (!appData) return;

			const { appQueryParams } = appData;
			appQueryParams.append('surveyId', surveyId);

			await fetchSurvey(appQueryParams);
		},
		[getApplicationData],
	);

	const sendSurveyResponse = async (payload: SurveyResponsePayload) => {
		const appData = getApplicationData();
		if (!appData) return;
		const { userFilter } = appData;

		payload.user = userFilter;

		try {
			await atomicHub.post(`/public-surveys/survey-response`, payload);
			notification.success({
				message: intl.formatMessage({ id: 'survey_feedback_send_response_success' }),
			});
		} catch (err) {
			const error = err as AxiosError<{ message: string }>;

			const errorMessage =
				error?.response?.data?.message ??
				intl.formatMessage({ id: 'survey_feedback_send_response_error' });
			console.error('Erro ao responder pesquisa:', error);
			notification.error({ message: errorMessage });
		} finally {
			setSurveyAvailable(null);
			setShouldOpen(false);
		}
	};

	const sendSurveyEvent = async (eventType: EventType) => {
		if (!surveyAvailable) return;

		const appData = getApplicationData();
		if (!appData) return;
		const { userFilter } = appData;

		const payload = {
			surveyId: surveyAvailable.id,
			user: userFilter,
			eventType,
			application: surveyAvailable.application,
		};

		try {
			await atomicHub.post(`/public-surveys/event`, payload);
			if (eventType === EventType.ignored) {
				notification.success({
					message: intl.formatMessage(
						{ id: 'survey_feedback_send_event_ignored' },
						{
							redisplayInDays: surveyAvailable.displayConfig.redisplayInDays,
							redisplayInDaysText:
								surveyAvailable.displayConfig.redisplayInDays === 1 ? 'dia' : 'dias',
						},
					) as string,
				});
			}
			if (eventType === EventType.discarded) {
				notification.success({
					message: intl.formatMessage({ id: 'survey_feedback_send_event_discarded' }),
				});
			}
		} catch (err) {
			const error = err as AxiosError<{ message: string }>;

			const errorMessage =
				error?.response?.data?.message ??
				intl.formatMessage({ id: 'survey_feedback_send_event_error' });
			console.error('Erro ao registrar evento da pesquisa:', error);
			notification.error({ message: errorMessage });
		} finally {
			setSurveyAvailable(null);
			setShouldOpen(false);
		}
	};

	useEffect(() => {
		if (!bitrix24) return;

		fetchSurveyPopup();
	}, [fetchSurveyPopup, bitrix24]);

	const contextValue = useMemo(
		() => ({
			fetchSurveyFakedoor,
			surveyAvailable,
			sendSurveyResponse,
			sendSurveyEvent,
			shouldOpen,
			setShouldOpen,
		}),
		[fetchSurveyFakedoor, surveyAvailable, sendSurveyResponse, shouldOpen],
	);

	return <SurveyContext.Provider value={contextValue}>{children}</SurveyContext.Provider>;
};

export const useSurvey = () => {
	const context = useContext(SurveyContext);
	if (!context) throw new Error('useSurvey must be used within a SurveyProvider');
	return context;
};
